import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';

// ─── EmailJS config — fill these in once your @vewr.io email is ready ────────
const EMAILJS_ENABLED = false; // flip to true when ready
const EMAILJS_SERVICE_ID  = 'YOUR_SERVICE_ID';
const EMAILJS_TEMPLATE_ID = 'YOUR_TEMPLATE_ID';
const EMAILJS_PUBLIC_KEY  = 'YOUR_PUBLIC_KEY';

const T = '#00A19C';
const T_DARK = '#007F7B';
const AMBER = '#A17900';
const ROSE = '#A10053';

const FAQS = [
  {
    q: 'Is Vewr HIPAA compliant?',
    a: `Vewr isn't currently HIPAA certified, but the way we store your data — encrypted on your device before it ever reaches us — meets the core technical standard HIPAA requires. Full compliance, including the legal and operational framework, is on our roadmap.`,
    tag: 'HIPAA',
  },
  {
    q: 'What if I lose access to my account?',
    a: `You sign in with your email — no password to forget, no recovery codes to lose track of.\n\nIf you ever get locked out, getting back in works the same way it always does: we send a one-time code to your email and you're in. That's it.`,
    tag: 'Access',
  },
  {
    q: 'Can a doctor access my records in an emergency if I\'m unconscious?',
    a: `Not automatically — and we'd rather be upfront about that.\n\nBecause only you can unlock your records, a doctor couldn't access them in an emergency unless you'd shared access with them ahead of time. If you want your doctor to have standing access, you can set that up in your account and revoke it whenever you want.\n\nWe're working on better emergency access options, but we won't promise something that doesn't exist yet.`,
    tag: 'Emergency',
  },
  {
    q: 'What is IPFS and why does Vewr use it?',
    a: `Instead of storing your files on a single server that could go down or get hacked, Vewr uses a distributed network called IPFS — the same type of resilient infrastructure that powers some of the most reliable systems on the internet.\n\nYour files aren't sitting in one place. They're distributed, encrypted, and tied to your account. No single point of failure, and no one can read them without your key.`,
    tag: 'Technology',
  },
  {
    q: 'How is this different from MyChart or my hospital\'s patient portal?',
    a: `Hospital portals give you a window into your records — but the files live on their servers, under their control. They decide what you can see, and you lose access if you switch providers or they change their system.\n\nVewr gives you your own copy that you keep no matter what. It's not a replacement for your hospital's portal — it's your personal backup that moves with you wherever you go.`,
    tag: 'Comparison',
  },
  {
    q: 'Who can actually see my records?',
    a: `Only you. When you upload a file, it's scrambled on your device using your personal key before it goes anywhere. That key never leaves your control.\n\nWe can't see your files. We have no backdoor. The only way anyone else could see your records is if you specifically share access with them.`,
    tag: 'Privacy',
  },
  {
    q: 'What file types can I upload?',
    a: `Right now: PDFs, JPGs, and PNGs. That covers most things — lab results, doctor's notes, prescriptions, vaccination records, and insurance documents.\n\nMore file types are on the way.`,
    tag: 'Files',
  },
  {
    q: 'If I delete a record, is it actually gone?',
    a: `What's important to understand is that your actual file never existed online to begin with — only an encrypted version that is completely unreadable without your personal key. Deleting a record removes it from your account and stops us from hosting it. Even in a scenario where a trace persists on the network, there is nothing there anyone could read. The key is yours, and without it the file is meaningless.`,
    tag: 'Deletion',
  },
];

function FaqItem({ faq, index, T, AMBER, ROSE }) {
  const [open, setOpen] = React.useState(false);
  const accent = [T, AMBER, ROSE][index % 3];
  return (
    <div
      style={{
        borderBottom: '1px solid #e8ecf0',
        transition: 'all 0.2s',
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', background: 'none', border: 'none',
          padding: '24px 0', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '16px', textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <span style={{
            fontSize: '10px', fontWeight: '700', letterSpacing: '0.8px',
            color: accent,
            background: `${accent}12`,
            border: `1px solid ${accent}25`,
            padding: '3px 8px', borderRadius: '4px', whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            {faq.tag.toUpperCase()}
          </span>
          <span style={{ fontSize: '16px', fontWeight: '600', color: '#0a0f1a', lineHeight: '1.4' }}>
            {faq.q}
          </span>
        </div>
        <div style={{
          width: '28px', height: '28px', borderRadius: '50%',
          background: open ? accent : '#f1f5f9',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, transition: 'all 0.2s',
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke={open ? 'white' : '#64748b'} strokeWidth="2.5"
            style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </button>
      {open && (
        <div style={{ paddingBottom: '24px', paddingLeft: '0' }}>
          {faq.a.split('\n\n').map((para, i) => (
            <p key={i} style={{
              fontSize: '15px', color: '#4a5568', lineHeight: '1.75',
              margin: i === 0 ? '0 0 14px 0' : '14px 0 0 0', maxWidth: '680px',
            }}>
              {para}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function FaqSection({ T, AMBER, ROSE }) {
  return (
    <section className="vw-section" style={{ padding: '120px 40px', background: '#f8fafc' }}>
      <div className="vw-faq-grid" style={{ maxWidth: '1080px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '80px', alignItems: 'start' }}>
        <div className="vw-faq-sticky" style={{ position: 'sticky', top: '80px' }}>
          <p style={{ color: AMBER, fontSize: '12px', fontWeight: '700', letterSpacing: '2px', margin: '0 0 12px 0' }}>
            FAQ
          </p>
          <h2 style={{
            fontSize: 'clamp(26px, 3vw, 38px)', fontWeight: '800',
            letterSpacing: '-1px', margin: '0 0 16px 0', color: '#0a0f1a', lineHeight: '1.15',
          }}>
            The questions you should be asking.
          </h2>
          <p style={{ fontSize: '15px', color: '#64748b', lineHeight: '1.7', margin: 0 }}>
            We'd rather answer the hard ones upfront than bury them in a terms of service.
          </p>
        </div>
        <div>
          {FAQS.map((faq, i) => (
            <FaqItem key={faq.q} faq={faq} index={i} T={T} AMBER={AMBER} ROSE={ROSE} />
          ))}
        </div>
      </div>
    </section>
  );
}


// ─── Portal Entry Modal ───────────────────────────────────────────────────────
const GATE_HASH = 'Get0ut123!@#';
const SESSION_KEY = 'vewr_portal_access';
function unlockPortal() { try { sessionStorage.setItem(SESSION_KEY, '1'); } catch {} }

function PortalModal({ onClose, navigate }) {
  const [step, setStep] = useState('password'); // password | role | npi | npiConfirm
  const [password, setPassword] = useState('');
  const [pwError, setPwError] = useState(false);
  const [shake, setShake] = useState(false);
  const [npiInput, setNpiInput] = useState('');
  const [npiData, setNpiData] = useState(null);
  const [npiLoading, setNpiLoading] = useState(false);
  const [npiError, setNpiError] = useState('');

  const submitPassword = (e) => {
    e.preventDefault();
    if (password === GATE_HASH) {
      unlockPortal();
      setStep('role');
      setPassword('');
    } else {
      setPwError(true);
      setShake(true);
      setPassword('');
      setTimeout(() => setShake(false), 500);
    }
  };

  const handleNpiLookup = async (e) => {
    e.preventDefault();
    const cleaned = npiInput.replace(/\D/g, '');
    if (cleaned.length !== 10) { setNpiError('NPI must be exactly 10 digits.'); return; }
    setNpiLoading(true); setNpiError('');
    try {
      const res = await fetch(`/api/npi?number=${cleaned}`);
      const data = await res.json();
      if (!data.result_count || data.result_count === 0) {
        setNpiError('No provider found for that NPI.'); setNpiLoading(false); return;
      }
      const r = data.results[0];
      if (r.basic?.status !== 'A') {
        setNpiError('This NPI is not active.'); setNpiLoading(false); return;
      }
      const isOrg = r.enumeration_type === 'NPI-2';
      const tax = (r.taxonomies || []).find(t => t.primary) || r.taxonomies?.[0] || {};
      setNpiData({
        npi: r.number,
        firstName: isOrg ? null : r.basic?.first_name,
        lastName: isOrg ? null : r.basic?.last_name,
        organizationName: isOrg ? r.basic?.organization_name : null,
        credential: r.basic?.credential || null,
        taxonomy: tax.desc || null,
        isOrg,
      });
      setStep('npiConfirm');
    } catch {
      setNpiError('Could not reach the NPPES registry. Please try again.');
    }
    setNpiLoading(false);
  };

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '24px', backdropFilter: 'blur(4px)',
  };
  const cardStyle = {
    background: '#0d1117', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '20px', padding: '44px',
    width: '100%', maxWidth: '420px', position: 'relative',
    boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
    fontFamily: '"Gotham SSm A","Gotham SSm B",system-ui,-apple-system,sans-serif',
  };

  return (
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{`
        @keyframes vw-shake {
          0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)}
          40%{transform:translateX(8px)} 60%{transform:translateX(-6px)} 80%{transform:translateX(6px)}
        }
        .vw-shake { animation: vw-shake 0.45s ease; }
      `}</style>

      <div style={cardStyle} className={shake ? 'vw-shake' : ''}>
        {/* Top accent */}
        <div style={{
          position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
          width: '60px', height: '3px', background: T, borderRadius: '0 0 4px 4px',
        }} />
        {/* Close */}
        <button onClick={onClose} style={{
          position: 'absolute', top: '16px', right: '16px',
          background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)',
          fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '4px 8px',
        }}>✕</button>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '28px' }}>
          <div style={{
            width: '34px', height: '34px', borderRadius: '8px', background: T,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 0 18px ${T}50`,
          }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </div>
          <span style={{ fontSize: '17px', fontWeight: '700', color: 'white', letterSpacing: '-0.3px' }}>
            Vewr Health
          </span>
        </div>

        {/* Step: password */}
        {step === 'password' && (
          <>
            <h2 style={{ color: 'white', fontSize: '20px', fontWeight: '700', margin: '0 0 8px 0', letterSpacing: '-0.4px' }}>
              Sign in to portal
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: '0 0 24px 0' }}>
              Enter your access code to continue.
            </p>
            <form onSubmit={submitPassword}>
              <input
                type="password" placeholder="Access code" value={password} autoFocus
                onChange={e => { setPassword(e.target.value); setPwError(false); }}
                style={{
                  width: '100%', padding: '12px 16px', boxSizing: 'border-box',
                  background: '#161b22', border: `1px solid ${pwError ? '#f87171' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '10px', color: 'white', fontSize: '15px',
                  fontFamily: 'inherit', outline: 'none', letterSpacing: '2px',
                }}
              />
              {pwError && <p style={{ color: '#f87171', fontSize: '12px', margin: '8px 0 0 0' }}>Incorrect code.</p>}
              <button type="submit" style={{
                width: '100%', marginTop: '14px', padding: '13px',
                background: T, color: 'white', border: 'none', borderRadius: '10px',
                fontSize: '15px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                boxShadow: `0 4px 16px ${T}40`,
              }}>Continue</button>
            </form>
          </>
        )}

        {/* Step: role selection */}
        {step === 'role' && (
          <>
            <h2 style={{ color: 'white', fontSize: '20px', fontWeight: '700', margin: '0 0 8px 0', letterSpacing: '-0.4px' }}>
              Who are you?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: '0 0 24px 0' }}>
              Select your role to continue to the right portal.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button onClick={() => navigate('/patient')} style={{
                padding: '18px 20px', background: '#161b22',
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px',
                color: 'white', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                transition: 'border-color 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
              >
                <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: '4px' }}>🧑‍⚕️ I'm a Patient</div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>
                  Upload, manage, and share your health records.
                </div>
              </button>
              <button onClick={() => setStep('npi')} style={{
                padding: '18px 20px', background: '#161b22',
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px',
                color: 'white', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                transition: 'border-color 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
              >
                <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: '4px' }}>🩺 I'm a Healthcare Provider</div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>
                  View records shared with you by your patients.
                </div>
              </button>
            </div>
          </>
        )}

        {/* Step: HCP NPI entry */}
        {step === 'npi' && (
          <>
            <button onClick={() => setStep('role')} style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)',
              fontSize: '13px', cursor: 'pointer', padding: '0 0 16px 0', fontFamily: 'inherit',
            }}>← Back</button>
            <h2 style={{ color: 'white', fontSize: '20px', fontWeight: '700', margin: '0 0 8px 0', letterSpacing: '-0.4px' }}>
              Verify your NPI
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: '0 0 24px 0' }}>
              Enter your 10-digit National Provider Identifier.
            </p>
            <form onSubmit={handleNpiLookup}>
              <input
                type="text" inputMode="numeric" placeholder="1234567890" autoFocus
                value={npiInput}
                onChange={e => { setNpiInput(e.target.value.replace(/\D/g, '').slice(0, 10)); setNpiError(''); }}
                style={{
                  width: '100%', padding: '12px 16px', boxSizing: 'border-box',
                  background: '#161b22', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '10px', color: 'white', fontSize: '16px', letterSpacing: '2px',
                  fontFamily: 'inherit', outline: 'none',
                }}
              />
              {npiError && <p style={{ color: '#f87171', fontSize: '12px', margin: '8px 0 0 0' }}>{npiError}</p>}
              <button type="submit" disabled={npiLoading || npiInput.length < 10} style={{
                width: '100%', marginTop: '14px', padding: '13px',
                background: npiInput.length < 10 ? 'rgba(0,161,156,0.4)' : T,
                color: 'white', border: 'none', borderRadius: '10px',
                fontSize: '15px', fontWeight: '600', fontFamily: 'inherit',
                cursor: npiInput.length < 10 ? 'not-allowed' : 'pointer',
              }}>
                {npiLoading ? 'Looking up…' : 'Look up NPI'}
              </button>
            </form>
          </>
        )}

        {/* Step: HCP NPI confirm */}
        {step === 'npiConfirm' && npiData && (
          <>
            <h2 style={{ color: 'white', fontSize: '20px', fontWeight: '700', margin: '0 0 6px 0', letterSpacing: '-0.4px' }}>
              Is this you?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: '0 0 20px 0' }}>
              Found in the NPPES registry.
            </p>
            <div style={{
              background: '#161b22', border: `1px solid ${T}30`,
              borderRadius: '12px', padding: '18px 20px', marginBottom: '20px',
            }}>
              <div style={{ color: 'white', fontSize: '17px', fontWeight: '700', marginBottom: '4px' }}>
                {npiData.isOrg
                  ? npiData.organizationName
                  : `${npiData.firstName || ''} ${npiData.lastName || ''}`.trim()}
                {npiData.credential ? `, ${npiData.credential}` : ''}
              </div>
              {npiData.taxonomy && (
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '4px' }}>{npiData.taxonomy}</div>
              )}
              <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', fontFamily: 'monospace' }}>
                NPI {npiData.npi}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setStep('npi'); setNpiData(null); }} style={{
                flex: 1, padding: '12px', background: 'transparent',
                border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px',
                color: 'rgba(255,255,255,0.6)', fontSize: '14px', fontWeight: '600',
                cursor: 'pointer', fontFamily: 'inherit',
              }}>Not me</button>
              <button onClick={() => {
                try { sessionStorage.setItem('vewr_pending_npi', JSON.stringify(npiData)); } catch {}
                navigate('/provider');
              }} style={{
                flex: 2, padding: '12px', background: T, border: 'none',
                borderRadius: '10px', color: 'white', fontSize: '14px', fontWeight: '600',
                cursor: 'pointer', fontFamily: 'inherit', boxShadow: `0 4px 14px ${T}40`,
              }}>Continue to sign in →</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LandingPage() {
  const [email, setEmail] = useState('');
  const [submitState, setSubmitState] = useState('idle');
  const [showPortalModal, setShowPortalModal] = useState(false);
  const navigate = useNavigate();


  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitState('loading');
    try {
      const res = await fetch('https://formspree.io/f/meenbyyb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (res.ok) {
        // Send confirmation email via EmailJS once @vewr.io is set up
        if (EMAILJS_ENABLED) {
          await emailjs.send(
            EMAILJS_SERVICE_ID,
            EMAILJS_TEMPLATE_ID,
            { to_email: email, to_name: email.split('@')[0] },
            EMAILJS_PUBLIC_KEY
          );
        }
        setEmail('');
        setSubmitState('success');
      } else {
        setSubmitState('error');
      }
    } catch {
      setSubmitState('error');
    }
  };

  return (
    <div style={{ fontFamily: '"Gotham SSm", "Gotham", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', margin: 0 }}>
      <style>{`
        @media (max-width: 768px) {
          .vw-nav { padding: 0 20px !important; }
          .vw-hero-section { padding: 90px 20px 60px !important; }
          .vw-hero-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .vw-hero-vault { display: none !important; }
          .vw-section { padding: 60px 20px !important; }
          .vw-stats-grid { grid-template-columns: 1fr !important; border-radius: 12px !important; }
          .vw-stats-grid > div { padding: 28px 24px !important; }
          .vw-steps-grid { grid-template-columns: 1fr 1fr !important; gap: 32px !important; }
          .vw-steps-line { display: none !important; }
          .vw-steps-grid > div { padding: 0 !important; }
          .vw-cards-grid { grid-template-columns: 1fr !important; }
          .vw-providers-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .vw-providers-card { display: none !important; }
          .vw-faq-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .vw-faq-sticky { position: static !important; }
          .vw-footer-cta { padding: 72px 20px !important; }
          .vw-timeline-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
        }
      `}</style>

      {/* ── NAV ── */}
      <nav className="vw-nav" style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
        height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 40px',
        background: 'rgba(6,11,20,0.7)', backdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '28px', height: '28px', borderRadius: '7px',
            background: T, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </div>
          <span style={{ fontSize: '16px', fontWeight: '700', color: 'white', letterSpacing: '-0.3px' }}>
            Vewr Health
          </span>
        </div>
        <button onClick={() => setShowPortalModal(true)} style={{
          padding: '7px 18px', background: 'rgba(0,161,156,0.15)',
          color: T, border: `1px solid ${T}50`,
          borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
          transition: 'all 0.2s',
        }}>
          Sign in to portal →
        </button>
      </nav>

      {/* ── HERO ── */}
      <section className="vw-hero-section" style={{
        minHeight: '100vh', background: '#060b14',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: '100px 40px 80px',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* dot grid background */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
        }} />
        {/* teal glow left */}
        <div style={{
          position: 'absolute', top: '20%', left: '-5%',
          width: '500px', height: '500px', borderRadius: '50%',
          background: `radial-gradient(circle, ${T}18 0%, transparent 65%)`,
          pointerEvents: 'none',
        }} />
        {/* rose glow right */}
        <div style={{
          position: 'absolute', bottom: '15%', right: '-5%',
          width: '400px', height: '400px', borderRadius: '50%',
          background: `radial-gradient(circle, ${ROSE}14 0%, transparent 65%)`,
          pointerEvents: 'none',
        }} />

        {/* two-column layout */}
        <div className="vw-hero-grid" style={{
          maxWidth: '1080px', margin: '0 auto', width: '100%',
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: '64px', alignItems: 'center', position: 'relative',
        }}>
          {/* LEFT: copy + form */}
          <div>
            {/* badge */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: `${T}12`, border: `1px solid ${T}30`,
              color: T, padding: '6px 14px', borderRadius: '100px',
              fontSize: '11px', fontWeight: '700', letterSpacing: '1px',
              marginBottom: '28px',
            }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: T, boxShadow: `0 0 8px ${T}` }} />
              YOUR RECORDS. YOUR KEYS.
            </div>

            <h1 style={{
              fontSize: 'clamp(32px, 4vw, 54px)', fontWeight: '800',
              lineHeight: '1.1', letterSpacing: '-2px',
              color: 'white', margin: '0 0 12px 0',
            }}>
              One place for every
              <br />record of your life.
              <br />
              <span style={{
                background: `linear-gradient(90deg, ${T}, #4dd9d5)`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                Under your control.
              </span>
            </h1>

            <p style={{
              fontSize: '16px', color: 'rgba(255,255,255,0.5)',
              lineHeight: '1.75', margin: '0 0 36px 0', maxWidth: '520px',
            }}>
              Your medical history is scattered across hospitals, portals, and providers
              you can barely log into. Vewr brings it together in one place — and only
              you hold the key.
            </p>

            {submitState === 'success' ? (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                background: `${T}15`, border: `1px solid ${T}40`,
                padding: '14px 20px', borderRadius: '12px',
                color: T, fontSize: '14px', fontWeight: '500',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                You're on the list — we'll be in touch.
              </div>
            ) : (
              <div>
                <form onSubmit={handleWaitlist} style={{
                  display: 'flex', background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '12px', padding: '5px', gap: '6px',
                  maxWidth: '400px',
                }}>
                  <input
                    type="email" required
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                      flex: 1, background: 'transparent', border: 'none', outline: 'none',
                      padding: '10px 14px', fontSize: '14px', color: 'white', minWidth: 0,
                    }}
                  />
                  <button type="submit" disabled={submitState === 'loading'} style={{
                    padding: '10px 20px', background: T, color: 'white',
                    border: 'none', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '13px', fontWeight: '700', whiteSpace: 'nowrap',
                    opacity: submitState === 'loading' ? 0.7 : 1, flexShrink: 0,
                  }}>
                    {submitState === 'loading' ? 'Joining...' : 'Join Waitlist'}
                  </button>
                </form>
                {submitState === 'error' && (
                  <p style={{ color: '#fc8181', fontSize: '13px', marginTop: '8px' }}>Something went wrong — try again.</p>
                )}
                <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: '12px', marginTop: '12px' }}>No spam. Early access only.</p>
              </div>
            )}

            {/* trust pills */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '32px' }}>
              {[
                { label: 'Sign in with email', icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg> },
                { label: 'Browser-encrypted', icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg> },
                { label: 'Works in any browser', icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg> },
              ].map(({ label, icon }) => (
                <div key={label} style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '100px', padding: '5px 11px',
                  color: 'rgba(255,255,255,0.4)', fontSize: '11px', fontWeight: '500',
                }}>
                  {icon}{label}
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: vault mockup */}
          <div className="vw-hero-vault" style={{ position: 'relative' }}>
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '300px', height: '300px', borderRadius: '50%',
              background: `radial-gradient(circle, ${T}20 0%, transparent 70%)`,
              pointerEvents: 'none',
            }} />
            <div style={{
              background: '#0d1117', borderRadius: '20px', padding: '24px',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: `0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)`,
              position: 'relative',
            }}>
              {/* vault header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '34px', height: '34px', borderRadius: '9px',
                    background: `${T}20`, border: `1px solid ${T}30`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'white' }}>Your Health Vault</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>End-to-end encrypted</div>
                  </div>
                </div>
                <div style={{
                  background: `${T}18`, color: T, border: `1px solid ${T}30`,
                  padding: '3px 9px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                }}>
                  🔒 Locked
                </div>
              </div>

              <div style={{ fontSize: '10px', fontWeight: '700', color: 'rgba(255,255,255,0.25)', letterSpacing: '1px', marginBottom: '10px' }}>
                YOUR RECORDS
              </div>

              {[
                { name: 'Blood Panel — Jan 2025', type: 'PDF', color: T },
                { name: 'MRI Scan — Dec 2024', type: 'IMG', color: AMBER },
                { name: 'Vaccination Record', type: 'PDF', color: T },
                { name: 'Prescription — Nov 2024', type: 'PDF', color: AMBER },
              ].map((r, i) => (
                <div key={r.name} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 12px', borderRadius: '9px',
                  background: i % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent',
                  border: '1px solid rgba(255,255,255,0.05)',
                  marginBottom: '6px',
                }}>
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '7px',
                    background: `${r.color}18`, border: `1px solid ${r.color}25`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={r.color} strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '12px', fontWeight: '500', color: 'rgba(255,255,255,0.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                  </div>
                  <span style={{ fontSize: '9px', fontWeight: '700', color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.05)', padding: '2px 6px', borderRadius: '3px' }}>{r.type}</span>
                </div>
              ))}

              <div style={{
                marginTop: '16px', padding: '12px',
                background: `${T}08`, border: `1px solid ${T}20`,
                borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '10px',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', lineHeight: '1.4' }}>
                  Only you can decrypt these files. Not even Vewr can read them.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PROBLEM ── */}
      <section className="vw-section" style={{ padding: '80px 40px', background: '#0d1117' }}>
        <div style={{ maxWidth: '1080px', margin: '0 auto' }}>
          <div className="vw-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', background: 'rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden' }}>
            {[
              { stat: '500+', label: 'data brokers registered to buy and sell your health data — in California alone', color: ROSE, source: 'CPPA', sourceUrl: 'https://privacy.ca.gov/drop/about-drop-and-the-delete-act/' },
              { stat: '0', label: 'times you were asked for permission', color: AMBER },
              { stat: '1', label: 'person should decide who sees your health records — you', color: T },
            ].map(({ stat, label, color, source, sourceUrl }) => (
              <div key={stat} style={{ padding: '40px 32px', background: '#0d1117' }}>
                <div style={{
                  fontSize: 'clamp(40px, 5vw, 64px)', fontWeight: '800',
                  letterSpacing: '-2px', color, lineHeight: 1, marginBottom: '12px',
                }}>
                  {stat}
                </div>
                <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.4)', margin: 0, lineHeight: '1.6' }}>
                  {label}
                  {source && (
                    <a href={sourceUrl} target="_blank" rel="noopener noreferrer" style={{
                      color: 'rgba(255,255,255,0.2)', fontSize: '11px', marginLeft: '6px',
                      verticalAlign: 'super', textDecoration: 'none',
                      borderBottom: '1px solid rgba(255,255,255,0.15)', lineHeight: 1,
                    }} title="Source: FTC Commercial Surveillance Report">
                      {source}
                    </a>
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="vw-section" style={{ padding: '120px 40px', background: '#ffffff' }}>
        <div style={{ maxWidth: '1080px', margin: '0 auto' }}>
          <div style={{ marginBottom: '72px' }}>
            <p style={{ color: AMBER, fontSize: '12px', fontWeight: '700', letterSpacing: '2px', margin: '0 0 12px 0' }}>
              HOW IT WORKS
            </p>
            <h2 style={{
              fontSize: 'clamp(30px, 4vw, 48px)', fontWeight: '800',
              letterSpacing: '-1.5px', margin: '0', color: '#0a0f1a', lineHeight: '1.1',
            }}>
              Built so even we<br />
              <span style={{ color: '#94a3b8' }}>can't read your files.</span>
            </h2>
          </div>

          <div className="vw-steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0', position: 'relative' }}>
            {/* connecting line */}
            <div className="vw-steps-line" style={{
              position: 'absolute', top: '28px', left: 'calc(12.5%)', right: 'calc(12.5%)',
              height: '1px', background: 'linear-gradient(90deg, transparent, #e2e8f0 20%, #e2e8f0 80%, transparent)',
              zIndex: 0,
            }} />

            {[
              {
                n: '01', title: 'Upload', desc: 'Sign in with your email and select any medical file from your device. No special software or accounts required.',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              },
              {
                n: '02', title: 'Encrypt', desc: 'AES-256-GCM encryption runs in your browser. The server never sees your plaintext file.',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              },
              {
                n: '03', title: 'Store', desc: 'Your encrypted file is pinned to the decentralized IPFS network. No central point of failure.',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>
              },
              {
                n: '04', title: 'Share', desc: 'Send a secure link to any provider for exactly the records you choose. Revoke access any time.',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              },
            ].map(({ n, title, desc, icon }) => (
              <div key={n} style={{ padding: '0 24px 0 0', position: 'relative', zIndex: 1 }}>
                <div style={{
                  width: '56px', height: '56px', borderRadius: '14px',
                  background: '#f8fafc', border: '1px solid #e2e8f0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '24px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}>
                  {icon}
                </div>
                <div style={{ fontSize: '11px', fontWeight: '700', color: AMBER, letterSpacing: '1.5px', marginBottom: '8px' }}>
                  {n}
                </div>
                <h3 style={{ fontSize: '17px', fontWeight: '700', margin: '0 0 10px 0', color: '#0a0f1a', letterSpacing: '-0.3px' }}>
                  {title}
                </h3>
                <p style={{ fontSize: '14px', color: '#64748b', margin: 0, lineHeight: '1.65' }}>
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOR YOU ── */}
      <section className="vw-section" style={{ padding: '120px 40px', background: '#ffffff' }}>
        <div style={{ maxWidth: '1080px', margin: '0 auto' }}>
          <div style={{ marginBottom: '64px' }}>
            <p style={{ color: T, fontSize: '12px', fontWeight: '700', letterSpacing: '2px', margin: '0 0 12px 0' }}>
              FOR YOU
            </p>
            <h2 style={{
              fontSize: 'clamp(28px, 4vw, 46px)', fontWeight: '800',
              letterSpacing: '-1.5px', margin: '0 0 16px 0', color: '#0a0f1a', lineHeight: '1.1',
            }}>
              Your records, finally<br />
              <span style={{ color: '#94a3b8' }}>working for you.</span>
            </h2>
            <p style={{ fontSize: '17px', color: '#64748b', margin: 0, maxWidth: '520px', lineHeight: '1.7' }}>
              Not for your insurer. Not for your hospital's IT department. Not for data brokers.
            </p>
          </div>

          <div className="vw-cards-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {[
              {
                icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
                title: 'Everything in one place',
                desc: 'Five hospitals. Three apps. A filing cabinet. Vewr pulls it all into a single encrypted vault that\'s actually yours.',
                accent: T,
              },
              {
                icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
                title: 'Only you can read it',
                desc: 'Encrypted in your browser before it ever leaves your device. Not your hospital\'s IT team. Not your insurer. Not a data broker. Yours.',
                accent: T,
              },
              {
                icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={AMBER} strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
                title: 'Share on your terms',
                desc: 'Give a new specialist exactly what they need — and nothing else. Revoke it the moment you\'re done.',
                accent: AMBER,
              },
              {
                icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={AMBER} strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
                title: 'Yours forever',
                desc: 'Switch insurers. Change doctors. Move across the country. Your records travel with you, not with your last provider.',
                accent: AMBER,
              },
            ].map(({ icon, title, desc, accent }) => (
              <div key={title} style={{
                padding: '32px',
                border: '1px solid #e8ecf0',
                borderRadius: '16px',
                background: '#fafbfc',
                borderTop: `3px solid ${accent}`,
              }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '12px',
                  background: `${accent}10`, border: `1px solid ${accent}20`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '20px',
                }}>
                  {icon}
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0a0f1a', margin: '0 0 10px 0', letterSpacing: '-0.3px' }}>
                  {title}
                </h3>
                <p style={{ fontSize: '15px', color: '#64748b', margin: 0, lineHeight: '1.65' }}>
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOR PROVIDERS ── */}
      <section className="vw-section" style={{ padding: '120px 40px', background: '#f8fafc' }}>
        <div className="vw-providers-grid" style={{ maxWidth: '1080px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
          <div>
            <p style={{ color: ROSE, fontSize: '12px', fontWeight: '700', letterSpacing: '2px', margin: '0 0 16px 0' }}>
              FOR HEALTHCARE PROVIDERS
            </p>
            <h2 style={{
              fontSize: 'clamp(28px, 3.5vw, 42px)', fontWeight: '800',
              letterSpacing: '-1.5px', margin: '0 0 20px 0', color: '#0a0f1a', lineHeight: '1.15',
            }}>
              Records patients<br />actually trust you with.
            </h2>
            <p style={{ fontSize: '16px', color: '#64748b', lineHeight: '1.75', margin: '0 0 36px 0' }}>
              Patients share access through Vewr — securely, on their terms, not through a fax or a broken portal.
              You get exactly what they choose to share, and they can take it back at any time.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
                'No logins or portals to manage',
                'Access is verified and controlled by the patient, not the provider',
                'Patients can revoke access at any time',
                'Full audit trail patients control',
              ].map((item) => (
                <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '50%',
                    background: T, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', flexShrink: 0, marginTop: '1px',
                  }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <span style={{ fontSize: '15px', color: '#374151', lineHeight: '1.5' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Provider card */}
          <div className="vw-providers-card" style={{
            background: '#0a0f1a', borderRadius: '20px', padding: '28px',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
          }}>
            {/* header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              paddingBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.07)',
              marginBottom: '20px',
            }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '50%',
                background: `linear-gradient(135deg, ${T}, ${T_DARK})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '700', fontSize: '14px', color: 'white' }}>Dr. Sarah Chen</div>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>Primary Care · Stanford Health</div>
              </div>
              <div style={{
                background: `${T}20`, color: T,
                padding: '3px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                border: `1px solid ${T}30`,
              }}>
                Active
              </div>
            </div>

            <p style={{ fontSize: '11px', fontWeight: '600', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px', margin: '0 0 12px 0' }}>
              SHARED RECORDS
            </p>

            {[
              { name: 'Blood Panel — Jan 2025', type: 'PDF', ago: '3 days ago' },
              { name: 'MRI Scan — Dec 2024', type: 'IMG', ago: '3 days ago' },
              { name: 'Vaccination Record', type: 'PDF', ago: '3 days ago' },
            ].map((r) => (
              <div key={r.name} style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '11px 12px', borderRadius: '10px',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.06)',
                marginBottom: '8px',
              }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '8px',
                  background: `${T}18`, border: `1px solid ${T}25`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.name}
                  </div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>{r.ago}</div>
                </div>
                <span style={{
                  fontSize: '10px', fontWeight: '700', color: 'rgba(255,255,255,0.35)',
                  background: 'rgba(255,255,255,0.06)', padding: '2px 7px', borderRadius: '4px',
                }}>
                  {r.type}
                </span>
              </div>
            ))}

            <button style={{
              width: '100%', marginTop: '12px', padding: '11px',
              background: `${ROSE}10`, border: `1px solid ${ROSE}35`,
              borderRadius: '10px', color: ROSE,
              fontSize: '13px', fontWeight: '600', cursor: 'pointer',
              transition: 'all 0.2s',
            }}>
              Revoke all access
            </button>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <FaqSection T={T} AMBER={AMBER} ROSE={ROSE} />

      {/* ── COMPANY ROADMAP ── */}
      <section className="vw-section" style={{ padding: '100px 40px', background: '#0d1117' }}>
        <div style={{ maxWidth: '1080px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <p style={{ color: T, fontSize: '12px', fontWeight: '700', letterSpacing: '2px', margin: '0 0 12px 0' }}>
              WHERE WE'RE HEADED
            </p>
            <h2 style={{
              fontSize: 'clamp(26px, 3vw, 38px)', fontWeight: '800',
              letterSpacing: '-1px', color: 'white', margin: '0 0 16px 0', lineHeight: 1.15,
            }}>
              Built in the open. Growing with you.
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '16px', margin: '0 auto', maxWidth: '520px', lineHeight: 1.7 }}>
              Here's exactly what's live, what we're building next, and where Vewr is headed.
            </p>
          </div>

          {/* Timeline — 3 columns on desktop, stacked on mobile */}
          <div className="vw-timeline-grid" style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px', position: 'relative',
          }}>
            {[
              {
                phase: 'Phase 1',
                status: 'Live Now',
                color: T,
                title: 'Your Private Vault',
                done: true,
                points: [
                  'Upload any medical file — PDF, JPG, PNG',
                  'AES-256-GCM encryption runs in your browser',
                  'Stored on decentralized IPFS — no single point of failure',
                  'Share links with custom expiry and instant revoke',
                  'Tag records by type, add names, dates, and notes',
                  'View who opened your shared links and when',
                ],
              },
              {
                phase: 'Phase 2',
                status: 'Up Next',
                color: AMBER,
                title: 'Smarter Sharing',
                done: false,
                points: [
                  'Provider portal with verified healthcare accounts',
                  'Request access to specific records from a provider',
                  'Full audit log — who accessed what, and when',
                  'Mobile app for iOS and Android',
                ],
              },
              {
                phase: 'Phase 3',
                status: 'On the Horizon',
                color: ROSE,
                title: 'Healthcare Infrastructure',
                done: false,
                points: [
                  'HIPAA-certified for clinics and hospital systems',
                  'Integration with major EHR platforms',
                  'Provider-initiated record requests with patient approval',
                  'Enterprise audit trail and access management',
                ],
              },
            ].map((phase, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Phase dot + label */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '50%',
                    background: `${phase.color}20`, border: `2px solid ${phase.color}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {phase.done ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={phase.color} strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    ) : (
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: `${phase.color}50` }} />
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px', marginBottom: '2px' }}>
                      {phase.phase}
                    </div>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: phase.color, letterSpacing: '0.5px' }}>
                      {phase.status}
                    </div>
                  </div>
                </div>

                {/* Card */}
                <div style={{
                  background: phase.done ? `${phase.color}08` : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${phase.done ? phase.color + '30' : 'rgba(255,255,255,0.06)'}`,
                  borderRadius: '16px', padding: '24px',
                  flex: 1,
                }}>
                  <h3 style={{
                    color: 'white', fontSize: '17px', fontWeight: '700',
                    margin: '0 0 18px 0', letterSpacing: '-0.3px',
                  }}>
                    {phase.title}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {phase.points.map((point, j) => (
                      <div key={j} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                        <div style={{
                          width: '16px', height: '16px', borderRadius: '50%',
                          background: `${phase.color}15`, border: `1px solid ${phase.color}30`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, marginTop: '2px',
                        }}>
                          {phase.done ? (
                            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={phase.color} strokeWidth="3.5">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          ) : (
                            <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: `${phase.color}60` }} />
                          )}
                        </div>
                        <p style={{
                          color: phase.done ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.3)',
                          fontSize: '13px', margin: 0, lineHeight: '1.6',
                        }}>
                          {point}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER CTA ── */}
      <section className="vw-footer-cta" style={{
        padding: '120px 40px', background: '#060b14',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '700px', height: '400px', borderRadius: '50%',
          background: `radial-gradient(ellipse, ${T}14 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />
        <div style={{ position: 'relative', maxWidth: '560px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: '800',
            letterSpacing: '-2px', color: 'white', margin: '0 0 16px 0', lineHeight: '1.1',
          }}>
            Own your health data.
          </h2>
          <p style={{ fontSize: '17px', color: 'rgba(255,255,255,0.45)', margin: '0 0 44px 0' }}>
            Early access coming soon.
          </p>
          {submitState === 'success' ? (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '10px',
              background: `${T}15`, border: `1px solid ${T}40`,
              padding: '14px 24px', borderRadius: '12px',
              color: T, fontSize: '15px', fontWeight: '500',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              You're on the list.
            </div>
          ) : (
            <form onSubmit={handleWaitlist} style={{
              display: 'flex', background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '14px', padding: '6px', gap: '6px',
              maxWidth: '440px', margin: '0 auto',
            }}>
              <input
                type="email" required
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  flex: 1, background: 'transparent', border: 'none', outline: 'none',
                  padding: '10px 14px', fontSize: '14px', color: 'white', minWidth: 0,
                }}
              />
              <button type="submit" disabled={submitState === 'loading'} style={{
                padding: '10px 22px', background: T, color: 'white',
                border: 'none', borderRadius: '9px', cursor: 'pointer',
                fontSize: '14px', fontWeight: '600', flexShrink: 0,
                opacity: submitState === 'loading' ? 0.7 : 1,
              }}>
                {submitState === 'loading' ? 'Joining...' : 'Join Waitlist'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{
        padding: '24px 40px', background: '#060b14',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '22px', height: '22px', borderRadius: '5px',
            background: T, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>Vewr Health</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: '12px', margin: 0 }}>
            © {new Date().getFullYear()} Vewr Health · Your data, your keys.
          </p>
          <a href="/terms" style={{ color: 'rgba(255,255,255,0.25)', fontSize: '12px', textDecoration: 'none', transition: 'color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.color = T}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.25)'}>
            Terms of Service
          </a>
          <a href="/privacy" style={{ color: 'rgba(255,255,255,0.25)', fontSize: '12px', textDecoration: 'none', transition: 'color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.color = T}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.25)'}>
            Privacy Policy
          </a>
        </div>
      </footer>

      {showPortalModal && (
        <PortalModal onClose={() => setShowPortalModal(false)} navigate={navigate} />
      )}
    </div>
  );
}

export default LandingPage;
